import { AxiosResponse } from 'axios'
import { api } from 'boot/axios'
import { PointHistory, RawPointHistory } from '.'
import { generateMediaUrl } from '../helper'
import {
  AvailableCard,
  Profile,
  ProfilePdpa,
  RawProfile,
  UpdateProfileParam,
  UpdateProfilePdpaParam,
  RawAvailableCard,
  RawUpdateProfileResponse,
  RawUpdateProfilePdpaResponse,
  Card,
  CardType,
  MemberLevels,
  RawMemberLevels,
  NextLevel,
  IAcceptConsent,
  IProfileConsent,
  RawProfleAcceptConsent,
} from './models'

export const sendPdpaForm = async (
  first_name: string,
  last_name: string,
  email: string,
  files: Blob | string,
  claim_type: string,
  claim_detail: string
): Promise<void> => {
  const bodyFormData = new FormData()
  bodyFormData.append('first_name', first_name)
  bodyFormData.append('last_name', last_name)
  bodyFormData.append('email', email)
  bodyFormData.append('files', files)
  bodyFormData.append('claim_type', claim_type)
  bodyFormData.append('claim_detail', claim_detail)
  await api({
    method: 'post',
    url: 'private/v1/pdpa/',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: bodyFormData,
  })
}

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  await api.post('private/v1/profile/change_password/', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export const getProfile = async (): Promise<Profile> => {
  const response: AxiosResponse<RawProfile> = await api.get(
    'private/v1/profile/'
  )

  return <Profile>{
    id: response.data.id,
    phoneCode: response.data.phone_code,
    phoneNumber: response.data.phone_number,
    firstName: response.data.first_name,
    lastName: response.data.last_name,
    gender: response.data.gender,
    birthdate: response.data.birthdate,
    address: response.data.address,
    city: response.data.city,
    zip: response.data.zip,
    branch: response.data.branch,
    email: response.data.email,
    optionalData: response.data.optional_datas,
    optional_fields: response.data.optional_fields,
    acceptedConsent: <IAcceptConsent>{
      term: response.data.accepted_consent.term,
      privacy: response.data.accepted_consent.privacy,
      member: response.data.accepted_consent.member,
      termAcceptDatetime: response.data.accepted_consent.term_accept_datetime,
      privacyAcceptDatetime:
        response.data.accepted_consent.privacy_accept_datetime,
    },
  }
}

export const updateProfile = async (
  profile: UpdateProfileParam
): Promise<Profile> => {
  const response: AxiosResponse<RawUpdateProfileResponse> = await api.patch(
    'private/v1/profile/',
    {
      first_name: profile.firstName,
      last_name: profile.lastName,
      gender: profile.gender,
      birthdate: profile.birthdate,
      address: profile.address,
      city: profile.city,
      zip: profile.zip,
      email: profile.email,
      branch: profile.branch,
      optional_datas: profile.optionalData,
    }
  )

  return <Profile>{
    firstName: response.data.first_name,
    lastName: response.data.last_name,
    gender: response.data.gender,
    birthdate: response.data.birthdate,
    address: response.data.address,
    city: response.data.city,
    zip: response.data.zip,
    email: response.data.email,
    optionalData: response.data.optional_datas,
  }
}

export const updateProfilePdpa = async (
  profile: UpdateProfilePdpaParam
): Promise<ProfilePdpa> => {
  const response: AxiosResponse<RawUpdateProfilePdpaResponse> = await api.patch(
    'private/v1/profile-pdpa/',
    {
      email: profile.email,
      consent_remark: profile.claim_type,
    }
  )

  return <ProfilePdpa>{
    email: response.data.email,
    claim_type: response.data.claim_type,
  }
}

export const getAvailableCard = async (): Promise<AvailableCard> => {
  const response: AxiosResponse<RawAvailableCard> = await api.get(
    'private/v1/profile/card/'
  )

  return <AvailableCard>{
    count: response.data.count,
    next: response.data.next,
    previous: response.data.previous,
    results: response.data.results.map(
      (elem) =>
        <Card>{
          id: elem.id,
          cardId: elem.card_id,
          customCardId: elem.custom_card_id,
          point: elem.point,
          totalPoint: elem.total_point,
          totalValue: elem.total_value,
          expirePoint: elem.expire_point,
          updatedAt: elem.updated_at,
          type: <CardType>{
            id: elem.type.id,
            name: elem.type.name,
            description: elem.type.description,
            image: elem.type.image,
            isDefault: elem.type.is_default,
          },
          fallbackDate: elem.fallback_date,
          isPointExpire: elem.is_point_expire,
          fallbackType: elem.fallback_type
            ? <CardType>{
                id: elem.fallback_type.id,
                name: elem.fallback_type.name,
                description: elem.fallback_type.description,
                image: elem.fallback_type.image,
                isDefault: elem.fallback_type.is_default,
              }
            : null,
          nextLevel: elem.next_level
            ? <NextLevel>{
                adjustDate: elem.next_level.adjust_date,
              }
            : null,
        }
    ),
  }
}

export const getPointHistory = async (
  cardId: number
): Promise<PointHistory[]> => {
  const response: AxiosResponse<RawPointHistory[]> = await api.get(
    `private/v1/profile/card/${cardId}/history/`
  )

  return response.data.map(
    (elem, idx) =>
      <PointHistory>{
        id: idx,
        transactionId: elem.transaction_id,
        type: elem.type,
        branch: elem.branch,
        referenceId: elem.reference_id,
        point: elem.point,
        remark: elem.remark,
        action: elem.action,
        createdAt: elem.created_at,
      }
  )
}

export const getAllMemberLevels = async (): Promise<MemberLevels[]> => {
  const response: AxiosResponse<RawMemberLevels[]> = await api.get(
    'private/v1/profile/member_level/'
  )
  return response.data.map(
    (elem) =>
      <MemberLevels>{
        pk: elem.pk,
        cardType: elem.card_type,
        cardTypeDetail: {
          id: elem.card_type_detail.id,
          name: elem.card_type_detail.name,
          description: elem.card_type_detail.description,
          image: generateMediaUrl(elem.card_type_detail.image),
          isDefault: elem.card_type_detail.is_default,
        },
        startRange: elem.start_range,
        endRange: elem.end_range,
      }
  )
}

export const updateAcceptConsent = async (
  term: number | null,
  privacy: number | null,
  term_accept_datetime: string | null,
  privacy_accept_datetime: string | null
): Promise<IAcceptConsent> => {
  const response: AxiosResponse<IAcceptConsent> = await api.post(
    'private/v1/profile/consent',
    {
      term: term,
      privacy: privacy,
      term_accept_datetime: term_accept_datetime,
      privacy_accept_datetime: privacy_accept_datetime,
    }
  )
  return response.data
}

export const updateProfileConsent = async (
  data: Array<IProfileConsent>
): Promise<IProfileConsent> => {
  const response: AxiosResponse<IProfileConsent> = await api.post(
    'private/v1/profile/profile_consent',
    data?.map((item) => {
      return {
        id: item.id,
        is_allowed: item.isAllowed,
      }
    })
  )
  return response.data
}

export const getConsentMember = async (): Promise<IProfileConsent[]> => {
  const response: AxiosResponse<RawProfleAcceptConsent[]> = await api.get(
    'private/v1/profile/profile_consent'
  )

  return response?.data?.map(
    (elem) =>
      <IProfileConsent>{
        id: elem.id,
        consentId: elem.consent_id,
        isAllowed: elem.is_allowed,
        createdAt: elem.created_at,
        labelEn: elem.label_en,
        labelTh: elem.label_th,
      }
  )
}

export default {
  changePassword,
  getProfile,
  updateProfile,
  updateProfilePdpa,
  getAvailableCard,
  getPointHistory,
  getAllMemberLevels,
  updateAcceptConsent,
}
